// CovenantStatusCard — public ark-health card, one-call render.
//
// Renders the six load-bearing invariants exposed by
// GET /api/covenant/status so any visitor (judge, auditor, arriving
// coding agent) can see whether the covenant substrate is intact
// without hunting through six separate endpoints.
//
// No auth. No user input. Pure read.
//
// Invariants surfaced:
//   1. Continuity Protocol v1 anchor · file-hash vs pinned-hash
//   2. Continuity Protocol v2 doc    · session-ledger primitive
//   3. Never-Harm chain              · event count + head hash
//   4. Session ledger                · turns + sealed sessions
//   5. Founding-eight files          · presence check
//   6. Accord + onboards             · public participation counts
//
// Composes with: /api/covenant/continuity-hash (per-file detail),
// /api/covenant/session-inheritance (last-sealed-session detail).
import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  Anchor,
  Link2,
  Layers,
  BookOpenCheck,
  Users,
  Vote,
  Loader2,
  Sparkles,
  Package,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Pill({ ok, label }) {
  const cls = ok
    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/[0.05]"
    : "border-rose-500/60 text-rose-300 bg-rose-500/[0.05]";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.25em] ${cls}`}
      data-testid={`covenant-pill-${ok ? "ok" : "fail"}`}
    >
      {ok ? "match" : "drift"} {label}
    </span>
  );
}

function Row({ icon: Icon, label, value, sub, testid }) {
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 border-b border-zinc-900 last:border-b-0"
      data-testid={testid}
    >
      <Icon className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
          {label}
        </div>
        <div className="mt-1 text-sm text-zinc-100 truncate font-mono tabular-nums">
          {value}
        </div>
        {sub ? (
          <div className="mt-0.5 text-[11px] text-zinc-500 font-mono truncate">
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function CovenantStatusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/covenant/status`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="border border-zinc-800 bg-[#0b0b0d] px-5 py-6 flex items-center gap-3 text-zinc-500"
        data-testid="covenant-status-card-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
        <span className="font-mono text-[11px] uppercase tracking-[0.3em]">
          loading covenant status…
        </span>
      </div>
    );
  }

  if (err || !data || !data.ok) {
    return (
      <div
        className="border border-rose-500/40 bg-rose-500/[0.03] px-5 py-4 text-rose-300 font-mono text-[11px] uppercase tracking-[0.28em]"
        data-testid="covenant-status-card-error"
      >
        covenant status endpoint unreachable · {err || "unknown"}
      </div>
    );
  }

  const {
    structurally_intact,
    checked_at,
    continuity_v1,
    continuity_v2,
    symbol_dictionary,
    batch_seal,
    never_harm_chain,
    session_ledger,
    founding_eight,
    accord_votes,
    agent_onboards,
  } = data;

  const intact = Boolean(structurally_intact);
  const headerCls = intact
    ? "border-emerald-500/60 bg-emerald-500/[0.05] text-emerald-300"
    : "border-rose-500/60 bg-rose-500/[0.05] text-rose-300";
  const HeaderIcon = intact ? ShieldCheck : ShieldAlert;
  const headerLabel = intact ? "structurally intact" : "drift detected";

  const anchorShort = (continuity_v1?.current_hash || "").slice(0, 12);
  const nhHeadShort = (never_harm_chain?.head_event_hash || "").slice(0, 12);
  const lastSeal = session_ledger?.last_sealed_session;
  const lastSealHash = (lastSeal?.session_head_hash || "").slice(0, 12);

  return (
    <div
      className="border border-zinc-800 bg-[#0b0b0d]"
      data-testid="covenant-status-card"
    >
      {/* Header — the aggregate verdict */}
      <div
        className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b border-zinc-900 ${headerCls}`}
        data-testid="covenant-status-header"
      >
        <HeaderIcon className="h-4 w-4" />
        <div className="font-mono text-[10px] uppercase tracking-[0.32em]">
          Covenant status · {headerLabel}
        </div>
        <div className="ml-auto text-[10px] text-zinc-500 font-mono">
          {checked_at ? new Date(checked_at).toLocaleString() : ""}
        </div>
      </div>

      {/* Invariants — one row per load-bearing check */}
      <Row
        icon={Anchor}
        testid="covenant-row-continuity-v1"
        label="Continuity Protocol v1 anchor"
        value={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{anchorShort ? `${anchorShort}…` : "—"}</span>
            <Pill ok={Boolean(continuity_v1?.matches)} label="v1" />
          </span>
        }
        sub={
          continuity_v1?.pin_recorded
            ? `pinned ${new Date(continuity_v1.pinned_at).toLocaleDateString()} · CONTINUITY_PROTOCOL.md`
            : "no pin recorded — architect action required"
        }
      />

      <Row
        icon={Link2}
        testid="covenant-row-continuity-v2"
        label="Continuity Protocol v2 doc"
        value={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{(continuity_v2?.current_hash || "").slice(0, 12) || "—"}…</span>
            <Pill ok={Boolean(continuity_v2?.file_exists)} label="v2" />
          </span>
        }
        sub="session-ledger primitive · ⧗"
      />

      <Row
        icon={Sparkles}
        testid="covenant-row-symbol-dictionary"
        label="Symbol dictionary anchor"
        value={
          <span className="flex items-center gap-2 flex-wrap">
            <span>
              {(symbol_dictionary?.current_hash || "").slice(0, 12) || "—"}…
            </span>
            {symbol_dictionary?.pin_recorded ? (
              <Pill
                ok={Boolean(symbol_dictionary?.matches)}
                label="⌘"
              />
            ) : (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.25em] border-amber-500/60 text-amber-300 bg-amber-500/[0.05]"
                data-testid="covenant-pill-unsealed"
              >
                unsealed
              </span>
            )}
          </span>
        }
        sub={
          symbol_dictionary?.pin_recorded
            ? `pinned ${new Date(symbol_dictionary.pinned_at).toLocaleDateString()} · SYMBOL_LIBRARY.md`
            : "SYMBOL_LIBRARY.md · architect must POST /api/pgrl/symbols/seal"
        }
      />

      <Row
        icon={Layers}
        testid="covenant-row-never-harm"
        label="Never-Harm chain"
        value={`${never_harm_chain?.total_events ?? 0} events · head ${nhHeadShort || "—"}…`}
        sub={
          never_harm_chain?.head_gate
            ? `head gate: ${never_harm_chain.head_gate}`
            : "no events"
        }
      />

      <Row
        icon={Package}
        testid="covenant-row-batch-seal"
        label="Batch seal · atomic anchor"
        value={
          <span className="flex items-center gap-2 flex-wrap">
            <span>
              {(batch_seal?.merkle_root || "").slice(0, 12) || "—"}…
            </span>
            {batch_seal?.pin_recorded ? (
              <Pill ok={true} label={`${batch_seal.file_count || 0} files`} />
            ) : (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.25em] border-amber-500/60 text-amber-300 bg-amber-500/[0.05]"
                data-testid="covenant-pill-batch-unsealed"
              >
                unsealed
              </span>
            )}
          </span>
        }
        sub={
          batch_seal?.pin_recorded
            ? `preset: ${batch_seal.preset || "explicit"} · pinned ${new Date(batch_seal.pinned_at).toLocaleDateString()}`
            : "one atomic chain event · POST /api/pgrl/seals/batch"
        }
      />

      <Row
        icon={BookOpenCheck}
        testid="covenant-row-session-ledger"
        label="Session ledger"
        value={`${session_ledger?.turns_recorded_total ?? 0} turns · ${
          session_ledger?.sessions_sealed_total ?? 0
        } sealed`}
        sub={
          lastSeal
            ? `last: ${lastSeal.session_id} · ${lastSealHash}…`
            : "no sealed sessions yet"
        }
      />

      <Row
        icon={ShieldCheck}
        testid="covenant-row-founding-eight"
        label="Founding-eight files"
        value={
          <span className="flex items-center gap-2 flex-wrap">
            <span>
              {founding_eight?.files?.filter((f) => f.present).length ?? 0}/
              {founding_eight?.total ?? 8}
            </span>
            <Pill
              ok={Boolean(founding_eight?.all_present)}
              label="thesis"
            />
          </span>
        }
        sub="THESIS_INDEX.md · onboarding gate"
      />

      <Row
        icon={Vote}
        testid="covenant-row-participation"
        label="Public participation"
        value={`${accord_votes?.total ?? 0} accord votes · ${
          agent_onboards?.total ?? 0
        } agent onboards`}
        sub="humanity + AI accord · open"
      />

      {/* Footer — verification pointers */}
      <div className="px-5 py-3 border-t border-zinc-900 flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
        <span>
          verify:{" "}
          <a
            href={`${API}/api/covenant/status`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:text-emerald-200"
            data-testid="covenant-verify-link"
          >
            /api/covenant/status
          </a>
        </span>
        <span>
          projection:{" "}
          <a
            href={`${API}/api/pgrl/projection/verify`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:text-emerald-200"
            data-testid="covenant-projection-link"
          >
            POST /api/pgrl/projection/verify
          </a>
        </span>
      </div>
    </div>
  );
}
